import { useEffect, useState } from "react";
import api from "@/lib/api";

// Cache channel lists per guild so multiple selects on a page share one request.
const _cache = {};
const _pending = {};

function fetchChannels(guildId) {
  if (_cache[guildId]) return Promise.resolve(_cache[guildId]);
  if (!_pending[guildId]) {
    _pending[guildId] = api.get(`/guilds/${guildId}/discord/channels`)
      .then((r) => { _cache[guildId] = { linked: !!r.data.linked, channels: r.data.channels || [], categories: r.data.categories || [] }; return _cache[guildId]; })
      .catch(() => { _cache[guildId] = { linked: false, channels: [], categories: [] }; return _cache[guildId]; });
  }
  return _pending[guildId];
}

// Drop-in channel picker. Stores the channel/category NAME (matches how the bot resolves them),
// so it's compatible with every existing system. Falls back to a text input when the bot
// isn't linked in this environment. Set kind="category" to pick a channel category instead.
export function ChannelSelect({ guildId, value, onChange, placeholder, testid, className = "", kind = "text", extraOptions = [] }) {
  const [data, setData] = useState(_cache[guildId] || null);
  useEffect(() => {
    let alive = true;
    if (guildId) fetchChannels(guildId).then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [guildId]);

  const isCat = kind === "category";
  const isVoice = kind === "voice";
  const withVoice = kind === "text+voice";
  const ph = placeholder || (isCat ? "— select a category —" : isVoice ? "— select a voice channel —" : "— select a channel —");
  const prefix = isCat ? "" : isVoice ? "🔊 " : "#";
  const base = `mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt ${className}`;

  if (!data || !data.linked) {
    return (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={data ? ph : (isCat ? "category name" : isVoice ? "voice channel name" : "channel name")} className={base} />
    );
  }
  const list = isCat ? (data.categories || []) : isVoice ? (data.voice || []) : data.channels;
  const voiceList = withVoice ? (data.voice || []) : [];
  const known = list.some((c) => c.name === value) || voiceList.some((c) => c.name === value) || extraOptions.some((o) => o.value === value);
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
      <option value="">{ph}</option>
      {extraOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      {value && !known && <option value={value}>{prefix}{value}</option>}
      {withVoice && voiceList.length > 0 ? (
        <>
          <optgroup label="Text channels">{list.map((c) => <option key={c.id} value={c.name}>#{c.name}</option>)}</optgroup>
          <optgroup label="Voice channel chats">{voiceList.map((c) => <option key={c.id} value={c.name}>🔊 {c.name}</option>)}</optgroup>
        </>
      ) : list.map((c) => <option key={c.id} value={c.name}>{prefix}{c.name}</option>)}
    </select>
  );
}

export default ChannelSelect;
